import { removeSchema } from '@wix/wix-code-collections-api'
import {
  AppCollectionVisibility,
  createDeferred,
} from '@wix/wix-data-client-common-standalone'
import { getDeveloperMode } from '@wix/wix-data-client-common'
import openContentManager from '../panels/openContentManager'
import createExternalDbDriverPanelOpener from '../panels/openExternalDbDriverPanel'
import createCollectionPanelOpener from '../panels/openCreateCollectionPanel'
import openCollectionPermissionsPanel from '../panels/openCollectionPermissionsPanel'

import {
  openAutoCMSItemViewForCurrentDynamicPage,
  openAutoCMSNewItemView,
  openAutoCMSItemView,
  openAutoCMSCollectionView,
  openAutoCMSCollectionsList,
  openAutoCMSAdvancedSettings,
  openWixServices,
} from '../panels/openAutoCMS'

export default ({
  experimentsManager,
  i18n,
  logger,
  editorSdkProxy,
  collectionsApi,
  hostname,
  finalAppApiPromise,
  fedopsLogger,
  editorType,
  wixData,
  appCollectionVisibilityPreference,
  sdkVersion,
}) => {
  let collectionSchemaRefreshIsAllowed = true
  let deferredCollectionSchemaRefresh = null

  return {
    async removeCollection({ collectionId }) {
      await collectionsApi.execute(removeSchema(collectionId))
      ;(await finalAppApiPromise).emit('collectionRemoved', collectionId)
    },

    async onExternalDbDriverRemoved({ namespace }) {
      ;(await finalAppApiPromise).emit('externalDbDriverRemoved', namespace)
    },

    async openContentManager({ collectionId, origin, isModal }) {
      return openContentManager({
        hostname,
        editorSdkProxy,
        collectionName: collectionId,
        isModal,
        origin,
        wixData,
        collectionsApi,
        i18n,
        logger,
        appApi: await finalAppApiPromise,
        experimentsManager,
        sdkVersion,
      })
    },

    async openAutoCmsItemViewForCurrentDynamicPage({ origin }) {
      return openAutoCMSItemViewForCurrentDynamicPage({
        editorSdkProxy,
        collectionsApi,
        origin,
        wixData,
        hostname,
        i18n,
        logger,
        appApi: await finalAppApiPromise,
        experimentsManager,
        sdkVersion,
      })
    },

    async openAutoCmsNewItemView({ collectionId, origin }) {
      return openAutoCMSNewItemView({
        editorSdkProxy,
        collectionId,
        origin,
        wixData,
        collectionsApi,
        hostname,
        i18n,
        logger,
        appApi: await finalAppApiPromise,
        experimentsManager,
        sdkVersion,
      })
    },

    async openAutoCmsItemView({ collectionId, itemId, origin }) {
      return openAutoCMSItemView({
        editorSdkProxy,
        collectionId,
        itemId,
        origin,
        wixData,
        collectionsApi,
        hostname,
        i18n,
        logger,
        appApi: await finalAppApiPromise,
        experimentsManager,
        sdkVersion,
      })
    },

    async openAutoCmsCollectionView({ collectionId, origin }) {
      return openAutoCMSCollectionView({
        editorSdkProxy,
        collectionId,
        collectionsApi,
        origin,
        wixData,
        hostname,
        i18n,
        logger,
        appApi: await finalAppApiPromise,
        experimentsManager,
        sdkVersion,
      })
    },
    async openWixServices() {
      return openWixServices({ editorSdkProxy })
    },
    async openAutoCmsCollectionsList({ origin, collectionsGroup }) {
      return openAutoCMSCollectionsList({
        editorSdkProxy,
        origin,
        wixData,
        collectionsApi,
        hostname,
        i18n,
        logger,
        collectionsGroup,
        appApi: await finalAppApiPromise,
        experimentsManager,
        sdkVersion,
      })
    },

    async openAutoCmsAdvancedSettings({ origin }) {
      return openAutoCMSAdvancedSettings({
        editorSdkProxy,
        origin,
        wixData,
        collectionsApi,
        hostname,
        i18n,
        logger,
        appApi: await finalAppApiPromise,
        experimentsManager,
        sdkVersion,
      })
    },

    async openExternalDbDriverPanel(namespace) {
      return createExternalDbDriverPanelOpener({
        hostname,
        logger,
        editorSdkProxy,
        experimentsManager,
      })(namespace)
    },

    async openCreateCollectionPanel({
      isEditMode,
      collectionId,
      origin,
      clickedInSiteStructure,
      initialScreen,
      supportsOnlyMultipleItemCollections,
      namespace,
      features,
    }) {
      const appApi = await finalAppApiPromise

      try {
        await appApi.invokeInterceptors('BEFORE_CREATE_COLLECTION_PANEL_OPEN')
      } catch {
        return
      }

      const openCreateCollectionPanel = createCollectionPanelOpener({
        hostname,
        collectionsApi,
        fedopsLogger,
        logger,
        editorSdkProxy,
        i18n,
        editorType,
        initialScreen,
        wixData,
        appApi,
        experimentsManager,
        sdkVersion,
      })

      return openCreateCollectionPanel({
        isEditMode,
        collectionId,
        origin,
        clickedInSiteStructure,
        supportsOnlyMultipleItemCollections,
        namespace,
        features,
      })
    },

    async openCollectionPermissionsPanel({ collectionId }) {
      return openCollectionPermissionsPanel({
        hostname,
        editorSdkProxy,
        logger,
        collectionId,
        experimentsManager,
      })
    },

    async refreshCollectionSchemasWhenAllowed() {
      if (collectionSchemaRefreshIsAllowed) {
        return (await finalAppApiPromise).refreshSchemasCache()
      }

      if (deferredCollectionSchemaRefresh === null) {
        deferredCollectionSchemaRefresh = createDeferred()
      }
      return deferredCollectionSchemaRefresh.promise
    },

    disallowCollectionSchemaRefresh() {
      collectionSchemaRefreshIsAllowed = false
    },

    allowCollectionSchemaRefresh() {
      if (!collectionSchemaRefreshIsAllowed) {
        collectionSchemaRefreshIsAllowed = true
        if (deferredCollectionSchemaRefresh !== null) {
          const deferred = deferredCollectionSchemaRefresh
          const refresh = async () => {
            const api = await finalAppApiPromise
            try {
              const result = await api.refreshSchemasCache()
              deferred.resolve(result)
            } catch (error) {
              deferred.reject(error)
            }
          }
          refresh()
          deferredCollectionSchemaRefresh = null
        }
      }
    },

    getAppCollectionVisibility: async () =>
      (await appCollectionVisibilityPreference.get()) ??
      ((await getDeveloperMode(editorSdkProxy))
        ? AppCollectionVisibility.VISIBLE
        : AppCollectionVisibility.HIDDEN),

    setAppCollectionVisibility: async visibility => {
      await appCollectionVisibilityPreference.set(visibility)

      const appApi = await finalAppApiPromise
      await appApi.refreshSchemasCache()
    },
  }
}
